import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { RecoveryCodes } from "@/components/profile/RecoveryCodes";
import { MfaSetupFlow } from "@/components/profile/MfaSetupFlow";
import { MfaConfirmDialog } from "@/components/profile/MfaConfirmDialog";

export const MfaSettings = () => {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("idle"); // idle | setup | codes
  const [codes, setCodes] = useState([]);
  const [confirm, setConfirm] = useState(null); // "disable" | "regen"

  const load = () => api.get("/auth/mfa/status").then(({ data }) => setStatus(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const showCodes = (c) => { setCodes(c); setMode("codes"); load(); refresh?.(); };

  const onConfirm = async (password, code) => {
    const url = confirm === "disable" ? "/auth/mfa/disable" : "/auth/mfa/recovery-codes";
    try {
      const { data } = await api.post(url, { password, code });
      setConfirm(null);
      if (confirm === "disable") { toast.success("Two-factor authentication turned off"); setMode("idle"); load(); refresh?.(); }
      else showCodes(data.recovery_codes);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  if (!status) return null;
  return (
    <div className="bg-white border border-border rounded-md p-5 space-y-4" data-testid="mfa-settings">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading font-semibold flex items-center gap-2"><ShieldCheck size={16} /> Two-factor authentication</h2>
          <p className="text-xs text-muted-foreground mt-1">Ask for a code from Microsoft Authenticator every time you sign in.</p>
        </div>
        <Badge variant="outline" className={status.enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "text-slate-500"} data-testid="mfa-status-badge">
          {status.enabled ? "On" : "Off"}
        </Badge>
      </div>

      {mode === "setup" && <MfaSetupFlow onEnabled={showCodes} onCancel={() => setMode("idle")} />}
      {mode === "codes" && <RecoveryCodes codes={codes} email={user?.email} onDone={() => setMode("idle")} />}

      {mode === "idle" && !status.enabled && (
        <Button onClick={() => setMode("setup")} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="mfa-setup-btn">Set up two-factor</Button>
      )}
      {mode === "idle" && status.enabled && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600" data-testid="mfa-codes-remaining">Recovery codes left: <strong>{status.recovery_codes_remaining}</strong></p>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => setConfirm("regen")} data-testid="mfa-regen-btn">New recovery codes</Button>
            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setConfirm("disable")} data-testid="mfa-disable-btn">Turn off</Button>
          </div>
        </div>
      )}
      <MfaConfirmDialog open={!!confirm} mode={confirm} onClose={() => setConfirm(null)} onConfirm={onConfirm} />
    </div>
  );
};
